import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { allowanceMinutes, minutesUsedThisPeriod, walletView } from '@acs/shared';
import {
  Badge,
  Button,
  EmptyState,
  Icon,
  PageHeader,
  SegmentedControl,
  Skeleton,
  Sparkline,
  StatCard,
} from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { useI18n } from '../../i18n/index.js';
import { reconnectFirestore } from '../../shared/api/network.js';
import { formatTime } from '../../shared/lib/format.js';
import { useCurrentDay } from '../../shared/lib/useCurrentDay.js';
import { useMediaQuery } from '../../shared/lib/useMediaQuery.js';
import { PHONE_QUERY } from '../../shared/lib/breakpoints.js';
import type { AppointmentDoc, BusinessDoc } from '../../shared/api/converters.js';
import { todayIsoIn } from '../schedule-grid/index.js';
import { AppointmentMessage } from './AppointmentMessage.js';
import { BookingDialog } from './BookingDialog.js';
import { newBookingDate } from './bookingForm.js';
import { useDashboardStats, useUpcomingAppointments } from './hooks.js';
import { StatBadges } from './StatBadges.js';
import { NewBookingDialog } from './NewBookingDialog.js';
import { DAY_PARAM, WeekCalendar } from './WeekCalendar.js';

type ScheduleView = 'calendar' | 'list';
const VIEW_KEY = 'tc24.dashboardScheduleView';

/**
 * "Is it working?" in one screen (design template — Shell + Dashboard): a KPI
 * strip answering that question at a glance, then today's schedule as either
 * the week calendar or a grouped list.
 */
export function DashboardPage() {
  const { t, tag } = useI18n();
  const business = useActiveBusiness();
  const [view, setView] = useState<ScheduleView>(readView);
  /** The date the New booking dialog opens on, while it is open. */
  const [booking, setBooking] = useState<string | null>(null);
  const [params] = useSearchParams();
  // The same breakpoint `WeekCalendar` narrows its grid to one day at, and the same one §12 of
  // the stylesheet turns the fit-the-viewport dashboard back into a scrolling
  // page at. Read in JS rather than rendered twice and hidden with CSS, because
  // the two treatments do not share markup — and because the hidden one would
  // still hold a live Firestore subscription open.
  const phone = useMediaQuery(PHONE_QUERY);
  // This is the screen an owner leaves open in a tab and comes back to the next
  // morning. The bookings and calls are live listeners, but "today" — the
  // heading, the highlighted column, the KPI day and its seven-day window, the
  // list's Today/Tomorrow — was fixed when the page mounted. Keying the data
  // parts on the date remounts them when it changes, so every one of those is
  // recomputed and every listener restarts with a current range. The dialogs
  // sit outside the keys so a half-filled booking survives anything short of
  // midnight. The two keyed parts are siblings, so each needs its OWN key: with
  // one shared key React's keyed reconcile loses track of the old KPI strip on
  // every re-render and mounts a new one beneath it. Coming back after a long absence also renews the Firestore
  // connection, which a sleeping laptop can leave silently dead.
  const day = useCurrentDay(business.timezone, reconnectFirestore);

  const chooseView = (v: ScheduleView) => {
    setView(v);
    try {
      localStorage.setItem(VIEW_KEY, v);
    } catch {
      /* private mode — the choice just doesn't stick */
    }
  };

  const today = headingDate(new Date(), business.timezone, tag);

  return (
    <div className="dashboard-full">
      <PageHeader
        title={today}
        actions={
          <>
            <SegmentedControl
              aria-label={t('dashboard.scheduleView')}
              value={view}
              onChange={chooseView}
              options={[
                { value: 'calendar', label: t('dashboard.view.calendar'), icon: <Icon name="calendar" size={16} /> },
                { value: 'list', label: t('dashboard.view.list'), icon: <Icon name="list" size={16} /> },
              ]}
            />
            {/* The owner books a customer by hand — a walk-in, a call they took
                themselves (doc 43). */}
            {/* It opens on the day the calendar shows (doc 86); the list
                shows no day, so today. */}
            <Button
              leadingIcon={<Icon name="plus" size={16} />}
              onClick={() =>
                setBooking(
                  newBookingDate(view === 'calendar' ? params.get(DAY_PARAM) : null, todayIsoIn(business.timezone), phone),
                )
              }
            >
              {t('bookingForm.newButton')}
            </Button>
          </>
        }
      />

      {booking && <NewBookingDialog business={business} date={booking} onClose={() => setBooking(null)} />}

      {/* A phone gets one line of badges under the date; a wide screen gets the
          five tiles. See `StatBadges` for why the numbers are not labelled. */}
      {phone ? (
        <StatBadges key={`stats-${day}`} business={business} />
      ) : (
        <KpiStrip key={`stats-${day}`} business={business} />
      )}

      <div className="dashboard-body" key={`body-${day}`}>
        {view === 'calendar' ? (
          <WeekCalendar businessId={business.id} timezone={business.timezone} slotStepMin={business.slotGranularityMin} />
        ) : (
          <div className="dashboard-scroll">
            <UpcomingList businessId={business.id} timezone={business.timezone} slotStepMin={business.slotGranularityMin} />
          </div>
        )}
      </div>
    </div>
  );
}

/** Minutes used · calls answered · bookings · missed · seven-day volume. */
function KpiStrip({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();
  const { stats, isLoading } = useDashboardStats(business.id, business.timezone);

  if (isLoading || !stats) {
    return (
      <div className="kpi-strip">
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} height={104} radius="var(--tc-radius-md)" />
        ))}
      </div>
    );
  }

  const { callsToday, bookingsToday, missedToday, volume, todayIndex, deltaPct } = stats;

  return (
    <div className="kpi-strip">
      {/* First, ahead of the day's numbers. It is the only tile that says
          whether the assistant will still be answering next week, and the one
          an owner is warned about by SMS — it should not be the one they have
          to look for. */}
      <MinutesCard business={business} />
      <StatCard
        label={
          <>
            <Icon name="phone-incoming" size={15} /> {t('dashboard.kpi.callsAnswered')}
          </>
        }
        value={callsToday}
        trend={deltaPct == null ? undefined : deltaPct > 0 ? 'up' : deltaPct < 0 ? 'down' : 'flat'}
        delta={
          deltaPct == null
            ? t('dashboard.kpi.firstWeek')
            : t('dashboard.kpi.vsAverage', { delta: `${deltaPct > 0 ? '+' : ''}${deltaPct}` })
        }
      />
      <StatCard
        label={
          <>
            <Icon name="calendar" size={15} /> {t('dashboard.kpi.bookings')}
          </>
        }
        value={bookingsToday}
        trend="flat"
        delta={callsToday > 0 ? t('dashboard.kpi.ofCalls', { count: callsToday }) : t('dashboard.kpi.noCallsYet')}
      />
      <StatCard
        label={
          <>
            <Icon name="phone-missed" size={15} /> {t('dashboard.kpi.missed')}
          </>
        }
        value={missedToday}
        trend="flat"
        delta={t('dashboard.kpi.missedHint')}
      />
      <div className="tc-card kpi-chart">
        <div className="kpi-chart__head">
          <span>{t('dashboard.kpi.volume')}</span>
          <span className="kpi-chart__note">
            {t('dashboard.kpi.total', { count: volume.reduce((n, b) => n + b.count, 0) })}
          </span>
        </div>
        <Sparkline
          height={56}
          data={volume.map((b) => b.count)}
          labels={volume.map((b) => b.label)}
          highlightIndex={todayIndex}
          label={t('dashboard.kpi.volumeLabel', {
            series: volume.map((b) => `${b.date} ${b.count}`).join(', '),
          })}
        />
      </div>
    </div>
  );
}

/**
 * How many of this period's minutes are gone, on the dashboard (doc 26 §4).
 *
 * The same meter as Settings › Subscription, down to the 50%/80% thresholds the
 * warning SMS fires at — an owner must never be able to read two different
 * answers to "how close am I to the assistant going quiet" on two screens. It
 * sits beside the day's numbers because that is the screen they actually open.
 *
 * With no plan there is no allowance to measure against, so it shows the
 * minutes spent and says so rather than drawing a bar with no scale.
 */
function MinutesCard({ business }: { business: BusinessDoc }) {
  const { t } = useI18n();

  // Prepaid (doc 42/59): there is no fixed monthly allowance — the plan tier
  // on the subscription would read as e.g. 150 whatever the wallet holds. What
  // the owner needs is what new calls can still use: included + purchased,
  // the same number Settings › Billing shows.
  if (business.billingSummary.mode === 'prepaid_v1') {
    const available = Math.floor(walletView(business.billingSummary, new Date()).availableSeconds / 60);
    return (
      <StatCard
        label={
          <>
            <Icon name="clock" size={15} /> {t('dashboard.kpi.minutesAvailable')}
          </>
        }
        value={available}
        trend="flat"
        delta={t('dashboard.kpi.minutesForNewCalls')}
      />
    );
  }

  const allowance = allowanceMinutes(business);
  // Re-applies the writer's lazy period reset, so a counter whose window has
  // closed reads 0 here too instead of showing last month's total.
  const used = minutesUsedThisPeriod(business, new Date());

  if (allowance === null) {
    return (
      <StatCard
        label={
          <>
            <Icon name="clock" size={15} /> {t('dashboard.kpi.minutes')}
          </>
        }
        value={used}
        trend="flat"
        delta={t('dashboard.kpi.minutesNoPlan')}
      />
    );
  }

  const remaining = Math.max(0, allowance - used);
  const pct = Math.min(100, Math.round((used / allowance) * 100));
  const tone = pct >= 80 ? 'danger' : pct >= 50 ? 'warning' : 'ok';

  return (
    <StatCard
      label={
        <>
          <Icon name="clock" size={15} /> {t('dashboard.kpi.minutes')}
        </>
      }
      value={t('dashboard.kpi.minutesOf', { used, total: allowance })}
      trend="flat"
      delta={t('usage.remaining', { count: remaining })}
      chart={
        <div
          className={`usage-bar usage-bar--${tone} kpi-usage`}
          role="progressbar"
          aria-valuenow={used}
          aria-valuemin={0}
          aria-valuemax={allowance}
          aria-label={t('usage.title')}
        >
          <span className="usage-bar__fill" style={{ width: `${pct}%` }} />
        </div>
      }
    />
  );
}

function UpcomingList({
  businessId,
  timezone,
  slotStepMin,
}: {
  businessId: string;
  timezone: string;
  slotStepMin: number;
}) {
  const { t, tag } = useI18n();
  const { data, isLoading } = useUpcomingAppointments(businessId);
  // By id, like the calendar: a cancelled or moved booking leaves the live
  // list, and its dialog closes with it.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = selectedId ? (data?.find((a) => a.id === selectedId) ?? null) : null;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={64} radius="var(--tc-radius-sm)" />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        borderless
        icon={<Icon name="calendar" size={24} />}
        title={t('dashboard.empty.title')}
        description={t('dashboard.empty.description')}
        // No buttons at all. An empty schedule is the ordinary state of a
        // morning, not a fault to diagnose: "start a test call" reads as a
        // prompt to check the thing is even working, and "your number" sent an
        // owner into Settings to look at something they have already seen — on
        // the screen where they came to read their day. Both live where they
        // belong (the test-call page, Settings › Number).
      />
    );
  }

  // Group by local calendar day so the list reads "Today / Tomorrow / …".
  const groups = groupByDay(data, timezone, tag, {
    today: t('dashboard.today'),
    tomorrow: t('dashboard.tomorrow'),
  });

  return (
    <>
      {groups.map((group) => (
        <section key={group.key} style={{ marginBottom: 20 }}>
          <h2
            style={{
              margin: '0 0 8px',
              fontSize: 12,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'var(--tc-text-secondary)',
            }}
          >
            {group.label}
          </h2>
          <div className="tc-cardlist">
            {group.items.map((appt) => (
              <AppointmentRow key={appt.id} appt={appt} timezone={timezone} onOpen={() => setSelectedId(appt.id)} />
            ))}
          </div>
        </section>
      ))}
      <p className="muted" style={{ fontSize: 13 }}>
        <Badge tone="info" dot>{t('dashboard.live')}</Badge> {t('dashboard.liveHint')}
      </p>
      {selected && (
        <BookingDialog
          businessId={businessId}
          appt={selected}
          timezone={timezone}
          slotStepMin={slotStepMin}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
}

/** One booking; pressing it opens the same details dialog as the calendar
 * (doc 86) — changing the time and cancelling live there. */
function AppointmentRow({ appt, timezone, onOpen }: { appt: AppointmentDoc; timezone: string; onOpen(): void }) {
  const { t, tag } = useI18n();

  return (
    <button
      type="button"
      className="tc-cardlist__item"
      onClick={onOpen}
      style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', font: 'inherit', color: 'inherit' }}
    >
      <span className="mono" style={{ fontWeight: 600, flex: 'none', width: 52 }}>
        {formatTime(appt.start, timezone, tag)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{appt.serviceName}</div>
        <div className="muted" style={{ fontSize: 13 }}>
          {appt.customerName}
          {appt.staffName ? ` · ${appt.staffName}` : ''}
        </div>
        {/* What the caller said that no field could hold (doc 23 P1). On its
            own line and quoted, because it is the customer's words rather than
            our data — "for the spaniel, Rex" has to read as something someone
            said, not as another attribute of the booking. */}
        {appt.note && (
          <div className="appt-note" title={appt.note}>
            <Icon name="info" size={13} />
            {appt.note}
          </div>
        )}
        {/* What the caller asked the business to know or do (doc 28). */}
        {appt.message && <AppointmentMessage message={appt.message} />}
      </div>
      {/* A booking from the owner's own "Test call" holds a real slot, so it
          belongs on the list — but it must be obvious it isn't a customer.
          Same badge the call history uses. */}
      {appt.test && <Badge tone="info" size="sm">{t('dashboard.test')}</Badge>}
    </button>
  );
}

interface DayGroup {
  key: string;
  label: string;
  items: AppointmentDoc[];
}

/** Bucket appointments into local calendar days, labelled Today / Tomorrow.
 *  The two relative words are passed in rather than looked up, so this stays a
 *  pure function of its arguments. */
function groupByDay(
  appts: AppointmentDoc[],
  timezone: string,
  locale: string,
  relative: { today: string; tomorrow: string },
): DayGroup[] {
  const dayKey = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const long = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const now = new Date();
  const todayKey = dayKey(now);
  const tomorrowKey = dayKey(new Date(now.getTime() + 86_400_000));

  const groups = new Map<string, DayGroup>();
  for (const appt of appts) {
    const key = dayKey(appt.start);
    const label =
      key === todayKey
        ? `${relative.today} · ${long.format(appt.start)}`
        : key === tomorrowKey
          ? `${relative.tomorrow} · ${long.format(appt.start)}`
          : long.format(appt.start);
    const group = groups.get(key) ?? { key, label, items: [] };
    group.items.push(appt);
    groups.set(key, group);
  }
  return [...groups.values()];
}

/**
 * The dashboard heading date, with the DAY and MONTH in green and the WEEKDAY
 * last — "26 September · Friday".
 *
 * Built from `formatToParts` rather than a formatted string, because which words
 * are the day and the month — and what order they come in — is the locale's
 * business, not ours. "25 września" and "25 September" tint the right
 * pieces without either being special-cased.
 *
 * The weekday is then appended rather than left where the locale put it (which
 * for all four of ours is the front). The date is what identifies the day and
 * the weekday is context, so the identifying part reads first; keeping it in the
 * locale's own position would mean the sentence starts with the least specific
 * word on the line. It is split off with a middot rather than the locale's own
 * comma, because a comma after the date would read as a list.
 *
 * pl/de/fr lowercase their weekday and month names, so both are capitalised to
 * keep the title reading like a title.
 */
function headingDate(date: Date, timezone: string, locale: string): ReactNode {
  const capitalise = (v: string) => v.charAt(0).toLocaleUpperCase(locale) + v.slice(1);

  // No year: the dashboard is always "today", and the year now lives on the
  // calendar's own date button where paging can actually change it.
  const parts = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    day: 'numeric',
    month: 'long',
  }).formatToParts(date);

  const weekday = new Intl.DateTimeFormat(locale, { timeZone: timezone, weekday: 'long' }).format(date);

  return (
    <>
      {parts.map((part, i) => {
        const value = part.type === 'month' ? capitalise(part.value) : part.value;
        return part.type === 'day' || part.type === 'month' ? (
          <span key={i} className="dashboard-date__accent">
            {value}
          </span>
        ) : (
          value
        );
      })}
      <span className="dashboard-date__weekday">{capitalise(weekday)}</span>
    </>
  );
}

function readView(): ScheduleView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'calendar';
  } catch {
    return 'calendar';
  }
}
